import { useMemo } from "react"
import type { ReactNode } from "react"
import { CollectionPagination } from "./CollectionPagination"
import { CollectionToolbar } from "./CollectionToolbar"
import { DataTableV2 } from "./DataTableV2"
import { SkeletonRows } from "../Skeleton"
import { useCollection, type CollectionColumnDef } from "./useCollection"
import type { DataTableColumn } from "../DataTable"

export interface DataTableCollectionProps<RowT extends Record<string, unknown>> {
  columns: DataTableColumn<RowT>[]
  data: RowT[]
  caption?: ReactNode
  searchPlaceholder?: string
  searchable?: boolean
  pageSize?: number
  emptyTitle?: string
  emptyMessage?: string
  className?: string
  tableClassName?: string
  rowKey?: (row: RowT, index: number) => string | number
  views?: string
  urlSync?: 'query' | false
  loading?: boolean
  stableChipNames?: boolean
}

function defaultRowKey(row: Record<string, unknown>, index: number): string {
  return String(row.id ?? index)
}

function mapLegacyColumns<RowT extends Record<string, unknown>>(
  columns: DataTableColumn<RowT>[]
): CollectionColumnDef<RowT>[] {
  return columns.map((col) => {
    const def: CollectionColumnDef<RowT> = {
      id: col.key,
      header: col.header,
      cell: col.render,
      sortable: col.sortable !== false,
    }
    if (col.sortAccessor) def.sortAccessor = col.sortAccessor
    if (col.filterValue) def.searchValue = col.filterValue
    if (col.align) def.align = col.align
    return def
  })
}

export function DataTableCollection<RowT extends Record<string, unknown>>({
  columns,
  data,
  caption,
  searchPlaceholder,
  searchable = true,
  pageSize = 0,
  emptyTitle,
  emptyMessage,
  className,
  tableClassName,
  rowKey,
  views,
  urlSync = false,
  loading = false,
  stableChipNames = false,
}: DataTableCollectionProps<RowT>) {
  const collectionColumns = useMemo(() => mapLegacyColumns(columns), [columns])
  const paginate = pageSize > 0
  const collection = useCollection<RowT>({
    mode: "client",
    rows: data,
    columns: collectionColumns,
    pageSize: paginate ? pageSize : 25,
    paginate,
    urlSync,
    ...(views ? { views } : {}),
    getRowKey: rowKey ?? (defaultRowKey as (row: RowT, index: number) => string),
  })

  const showPagination = paginate && collection.page.totalPages > 1

  return (
    <div className={className}>
      {loading ? (
        <div data-collection data-collection-state="loading" className="rounded-surface border border-line bg-white shadow-card">
          <SkeletonRows rows={6} columns={collectionColumns.length || 4} />
        </div>
      ) : (
        <>
          {searchable ? (
            <CollectionToolbar collection={collection} searchPlaceholder={searchPlaceholder} stableChipNames={stableChipNames} />
          ) : null}
          <DataTableV2
            collection={collection}
            caption={typeof caption === "string" ? caption : undefined}
            emptyTitle={emptyTitle}
            emptyMessage={emptyMessage}
            tableClassName={tableClassName}
          />
          {showPagination ? <CollectionPagination collection={collection} /> : null}
        </>
      )}
    </div>
  )
}
